<script lang="ts">
  import FileUp from '@lucide/svelte/icons/file-up';

  import { getServices } from '../../app/context';
  import { importErrorMessage } from '../../app/routes';
  import { importShare } from '../../io/import';
  import { extractSharePayload } from '../../io/share-url';
  import { strings } from '../../strings';
  import Button from '../../ui/Button.svelte';
  import Dialog from '../../ui/Dialog.svelte';

  interface Props {
    open?: boolean;
    /** Imports a file and hands the list to the importer; resolves to an error message or null. */
    onfile: (file: File) => Promise<string | null>;
  }

  let { open = $bindable(false), onfile }: Props = $props();

  const services = getServices();
  const id = $props.id();
  let link = $state('');
  let error = $state('');
  let fileInput: HTMLInputElement | undefined = $state();

  $effect(() => {
    if (open) {
      link = '';
      error = '';
    }
  });

  function openLink() {
    const payload = extractSharePayload(link);
    if (payload === null) {
      error = strings.notALink;
      return;
    }
    try {
      const doc = importShare(payload, services);
      open = false;
      services.importer.offer(doc);
    } catch (cause) {
      error = importErrorMessage(cause);
    }
  }

  async function chooseFile(file: File | undefined) {
    if (!file) return;
    const message = await onfile(file);
    if (message === null) open = false;
    else error = message;
  }
</script>

<Dialog bind:open title={strings.importTitle} description={strings.importDescription}>
  <div class="flex flex-col gap-4">
    <input
      bind:this={fileInput}
      type="file"
      accept=".png,.json,image/png,application/json"
      class="sr-only"
      tabindex="-1"
      aria-hidden="true"
      onchange={(event) => {
        void chooseFile(event.currentTarget.files?.[0]);
        event.currentTarget.value = '';
      }}
    />
    <Button class="self-start" onclick={() => fileInput?.click()}>
      <FileUp size={16} aria-hidden="true" />
      {strings.chooseFile}
    </Button>
    <form
      class="flex flex-col gap-1"
      onsubmit={(event) => {
        event.preventDefault();
        openLink();
      }}
    >
      <label for="{id}-link" class="text-sm font-medium">{strings.pasteLinkLabel}</label>
      <div class="flex gap-2">
        <input
          id="{id}-link"
          bind:value={link}
          inputmode="url"
          autocomplete="off"
          spellcheck="false"
          aria-invalid={error !== '' ? true : undefined}
          aria-describedby={error !== '' ? `${id}-error` : undefined}
          class="min-h-10 min-w-0 flex-1 rounded-lg border border-line-strong/70 bg-surface px-3 text-base text-ink sm:text-sm"
        />
        <Button type="submit" variant="primary" disabled={link.trim() === ''}>
          {strings.openLink}
        </Button>
      </div>
    </form>
    {#if error !== ''}
      <p id="{id}-error" role="alert" class="rounded-lg bg-danger-soft p-3 text-sm">{error}</p>
    {/if}
  </div>
</Dialog>
